import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { PhoneMockup } from '../interactive/PhoneMockup';
import { appScreenshots, mascot } from '../../data/mock';
import { Share, Users, Sparkles, CalendarDays, MapPin, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ScrollSection = ({ pillar, index, setPhase, isMobile, t }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { margin: "-50% 0px -50% 0px" });

  useEffect(() => {
    if (isInView) {
      setPhase(index);
    }
  }, [isInView, index, setPhase]);

  const isEven = index % 2 === 0;
  const Icon = pillar.icon;

  return (
    <section ref={ref} className="h-screen w-full flex items-center justify-center px-4 md:px-6 lg:px-12 pointer-events-none snap-center pb-8 md:pb-0">
      <div className="w-full max-w-7xl relative pointer-events-none flex items-end md:items-center h-full">
        
        <AnimatePresence mode="wait">
          {isInView && (
            <motion.div 
              initial={{ opacity: 0, y: isMobile ? 50 : 0, x: isMobile ? 0 : (isEven ? -50 : 50) }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              exit={{ opacity: 0, y: isMobile ? 50 : 0, x: isMobile ? 0 : (isEven ? -50 : 50) }}
              transition={{ duration: 0.5 }}
              className={`pointer-events-auto w-full md:w-1/2 lg:max-w-xl flex flex-col bg-white/90 md:bg-transparent backdrop-blur-md md:backdrop-blur-none p-6 md:p-0 rounded-3xl md:rounded-none shadow-2xl md:shadow-none border border-slate-100 md:border-none items-center text-center ${!isEven ? 'md:ml-auto md:items-end md:text-right md:pl-8 lg:pl-12' : 'md:items-start md:text-left md:pr-8 lg:pr-12'}`}
            >
              <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-3 md:mb-4 leading-tight">
                {t(`zigzag.pillars.${index}.title`)}
              </h2>
              {pillar.hook && (
                <h3 className="text-xl md:text-2xl font-semibold text-[#0DA2E7] mb-3 md:mb-4">
                  {t(`zigzag.pillars.${index}.hook`)}
                </h3>
              )}
              <p className="text-lg md:text-xl text-slate-600 font-medium leading-relaxed">
                {t(`zigzag.pillars.${index}.description`)}
              </p>
              
              {pillar.id === 1 && (
                <div className="flex items-center gap-5 mt-8">
                  <div className="w-12 h-12 rounded-full bg-white shadow-md border border-slate-100 flex items-center justify-center hover:scale-110 transition-transform">
                    <img src={process.env.PUBLIC_URL + "/assets/instagram.png"} alt="Instagram" className="w-7 h-7 object-contain" />
                  </div>
                  <div className="w-12 h-12 rounded-full bg-white shadow-md border border-slate-100 flex items-center justify-center hover:scale-110 transition-transform">
                    <img src={process.env.PUBLIC_URL + "/assets/tiktok.png"} alt="TikTok" className="w-7 h-7 object-contain" />
                  </div>
                  <div className="w-12 h-12 rounded-full bg-white shadow-md border border-slate-100 flex items-center justify-center hover:scale-110 transition-transform text-slate-800">
                    <Globe className="w-6 h-6" />
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </section>
  );
};

export const ZigzagFeatures = () => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState(0);
  const [imageIndex, setImageIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const pillars = [
    {
      id: 1,
      icon: Share,
      images: [appScreenshots.map, appScreenshots.placeDetail, appScreenshots.myTrips],
      color: "bg-blue-50",
      iconColor: "text-blue-500"
    },
    {
      id: 2,
      hook: true,
      icon: MapPin,
      images: [appScreenshots.placeDetail, appScreenshots.myTrips],
      color: "bg-purple-50",
      iconColor: "text-purple-500"
    },
    {
      id: 3,
      hook: true,
      icon: Sparkles,
      images: [process.env.PUBLIC_URL + "/assets/flutter_31.jpg"],
      color: "bg-yellow-50",
      iconColor: "text-yellow-500"
    },
    {
      id: 4,
      hook: true,
      icon: Users,
      images: [appScreenshots.placesList, appScreenshots.placeDetailFull, appScreenshots.share],
      color: "bg-emerald-50",
      iconColor: "text-emerald-500"
    },
    {
      id: 5,
      hook: true,
      icon: CalendarDays,
      images: [
        appScreenshots.mapCTA,
        appScreenshots.tripPlanner,
        process.env.PUBLIC_URL + "/assets/flutter_14.jpg",
        process.env.PUBLIC_URL + "/assets/flutter_15.jpg"
      ],
      color: "bg-orange-50",
      iconColor: "text-orange-500"
    }
  ];

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setImageIndex(prev => prev + 1);
    }, 3500);
    return () => clearTimeout(timeout);
  }, [imageIndex]);

  useEffect(() => {
    setImageIndex(0);
  }, [phase]);

  // Phone positions: floats perfectly to the right when text is on the left, and vice versa.
  const phoneVariants = {
    0: { x: isMobile ? "-50%" : "calc(25vw - 50%)", y: isMobile ? "-70%" : "-50%", scale: isMobile ? 0.8 : 1 },
    1: { x: isMobile ? "-50%" : "calc(-25vw - 50%)", y: isMobile ? "-70%" : "-50%", scale: isMobile ? 0.8 : 1 },
    2: { x: isMobile ? "-50%" : "calc(25vw - 50%)", y: isMobile ? "-70%" : "-50%", scale: isMobile ? 0.8 : 1 },
    3: { x: isMobile ? "-50%" : "calc(-25vw - 50%)", y: isMobile ? "-70%" : "-50%", scale: isMobile ? 0.8 : 1 },
    4: { x: isMobile ? "-50%" : "calc(25vw - 50%)", y: isMobile ? "-70%" : "-50%", scale: isMobile ? 0.8 : 1 },
  };

  // Monkey positions: refined diagonal path that dodges the phone edges and respects screen bounds
  const monkeyPositions = {
    0: { left: isMobile ? "15%" : "15%", top: isMobile ? "5%" : "15%", scaleX: 1 },
    1: { left: isMobile ? "80%" : "46%", top: isMobile ? "10%" : "32%", scaleX: 1 },
    2: { left: isMobile ? "15%" : "54%", top: isMobile ? "15%" : "48%", scaleX: 1 },
    3: { left: isMobile ? "80%" : "68%", top: isMobile ? "20%" : "65%", scaleX: 1 },
    4: { left: isMobile ? "50%" : "82%", top: isMobile ? "25%" : "82%", scaleX: 1 },
  };

  return (
    <div id="how-it-works" className="relative w-full bg-slate-50">
      
      <div className="text-center max-w-3xl mx-auto pt-16 pb-8 px-6">
        <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-4">
          {t('zigzag.title')}
        </h2>
      </div>

      {/* Main Container - 500vh tall to hold 5 scrolling text sections */}
      <div className="relative w-full h-[500vh] snap-y snap-mandatory">
        
        {/* Sticky Layer - This stays on screen the whole time */}
        <div 
          style={{ position: 'sticky', top: 0, height: '100vh' }}
          className="w-full overflow-hidden pointer-events-none z-10"
        >
          
          {/* Floating Monkey */}
          <motion.div
            className="absolute z-40 drop-shadow-2xl"
            initial={false}
            animate={{
              left: monkeyPositions[phase].left,
              top: monkeyPositions[phase].top,
              scaleX: monkeyPositions[phase].scaleX,
              x: "-50%", // Centers on the left percentage
              y: 0
            }}
            transition={{ duration: 1.2, ease: "easeInOut" }}
          >
            <motion.img 
              src={mascot.main} 
              alt="Monito Mochilero"
              className="w-24 md:w-48 h-auto"
              animate={{ y: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
            />
          </motion.div>

          {/* Floating Phone */}
          <motion.div
            className="absolute top-1/2 left-1/2 z-10 pointer-events-auto"
            initial={phoneVariants[0]}
            animate={phoneVariants[phase]}
            transition={{ type: "spring", stiffness: 50, damping: 15, mass: 1 }}
          >
            <div className="relative w-64 md:w-72 group">
              <AnimatePresence mode="wait">
                <motion.div 
                  key={`bg-${phase}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 0.4, scale: 1.5 }}
                  exit={{ opacity: 0, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className={`absolute inset-0 rounded-full blur-3xl -z-10 ${pillars[phase].color}`} 
                />
              </AnimatePresence>

              {/* Prev Arrow */}
              {pillars[phase].images.length > 1 && (
                <button
                  onClick={() => setImageIndex(prev => (prev - 1 < 0 ? pillars[phase].images.length - 1 : prev - 1))}
                  className="absolute -left-5 md:-left-14 top-1/2 -translate-y-1/2 bg-white text-slate-800 p-2.5 rounded-full shadow-xl hover:scale-110 transition-all z-50 border border-slate-100 opacity-80 hover:opacity-100"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}

              <PhoneMockup currentScreen={pillars[phase].images[imageIndex % pillars[phase].images.length]} />

              {/* Next Arrow */}
              {pillars[phase].images.length > 1 && (
                <button
                  onClick={() => setImageIndex(prev => prev + 1)}
                  className="absolute -right-5 md:-right-14 top-1/2 -translate-y-1/2 bg-white text-slate-800 p-2.5 rounded-full shadow-xl hover:scale-110 transition-all z-50 border border-slate-100 opacity-80 hover:opacity-100"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>
          </motion.div>
          
        </div>

        {/* Scrolling Text Sections Layer */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none z-20">
          {pillars.map((pillar, index) => (
            <ScrollSection 
              key={pillar.id} 
              pillar={pillar} 
              index={index} 
              setPhase={setPhase} 
              isMobile={isMobile}
              t={t}
            />
          ))}
        </div>

      </div>

    </div>
  );
};
